import { createPatchScheduler } from './scheduler';
import { createNodeId } from './node-id';
import { createControlFlowRuntime, isControlFlowNode, } from './control-flow';
import { diffPropObjects, longestIncreasingSubsequenceIndices, mergeChildren, } from './reconcile';
import { effect, untrack } from '../state';
import { createFlushContext, disposeFlushContext, schedulePostCommitTask } from '../state/context';
import { createRoot, getOwner, onCleanup, runWithOwner } from '../state/scope';
import { materializeRuntimeNode } from './runtime-component';
import { THEME_SCOPE_PROP } from './internal-props';
import { getTheme, setTheme } from '../theme/theme_context';
import { isReactiveAccessor } from '../state/accessor';
function getRuntimeProcess() {
    if (typeof globalThis !== 'object' || globalThis === null || !('process' in globalThis)) {
        return undefined;
    }
    const runtimeProcess = globalThis.process;
    return runtimeProcess;
}
export function createPatchEmitter(options = {}) {
    let scheduler = null;
    let disposed = false;
    let emitterOwner = null;
    let disposeEmitterScope = null;
    const schedule = options.schedule ?? queueMicrotask;
    const flushContext = createFlushContext(schedule);
    const nodeScopes = new Map();
    const nodeMap = new Map();
    const controlChildren = new Map();
    const controlSelections = new Map();
    const warnedForNodes = new Set();
    const warnedForDuplicateKeys = new Set();
    const runtimeProcess = getRuntimeProcess();
    const warnOnMissingKeys = options.warnOnMissingKeys ??
        (runtimeProcess
            ? runtimeProcess.env?.NODE_ENV !== 'production'
            : true);
    const keyValidation = { warnOnMissingKeys, warnedForNodes, warnedForDuplicateKeys };
    const controlFlowRuntime = createControlFlowRuntime({
        ensureNodeId,
        getEmitterOwner: () => emitterOwner,
    });
    const emit = (patches) => {
        if (!scheduler || patches.length === 0) {
            return;
        }
        scheduler.enqueue(patches);
    };
    const disposeNodeScope = (nodeId) => {
        const dispose = nodeScopes.get(nodeId);
        if (!dispose) {
            return;
        }
        nodeScopes.delete(nodeId);
        dispose();
    };
    const mountNodeScope = (node, includeControlEffect = false) => {
        if (!nodeNeedsScope(node, includeControlEffect)) {
            return;
        }
        const register = () => createRoot((dispose) => {
            nodeScopes.set(node.id, dispose);
            if (node._runtimeDispose) {
                onCleanup(node._runtimeDispose);
            }
            registerPropBindings(node);
            registerReactiveText(node);
            if (includeControlEffect && isControlFlowNode(node)) {
                registerControlEffect(node, true);
            }
        });
        if (emitterOwner) {
            runWithOwner(emitterOwner, register);
            return;
        }
        register();
    };
    const registerPropBindings = (node) => {
        if (!shouldTrackProps(node)) {
            return;
        }
        const props = node.props ?? {};
        for (const [name, value] of Object.entries(props)) {
            if (!isReactiveAccessor(value) || isEventProp(name, value)) {
                continue;
            }
            let hasValue = false;
            let lastValue;
            const initial = value();
            if (initial !== undefined) {
                hasValue = true;
                lastValue = initial;
            }
            effect(() => {
                const next = value();
                if (next === undefined) {
                    if (hasValue) {
                        emit([{ op: 'remove-prop', id: node.id, name }]);
                        hasValue = false;
                    }
                    return;
                }
                if (!hasValue || !Object.is(next, lastValue)) {
                    lastValue = next;
                    hasValue = true;
                    emit([{ op: 'set-prop', id: node.id, name, value: next }]);
                }
            });
        }
    };
    const registerReactiveText = (node) => {
        if (!isReactiveTextCarrier(node)) {
            return;
        }
        const accessor = node.props?.value;
        if (!isReactiveTextAccessor(accessor)) {
            return;
        }
        let lastValue = accessor();
        effect(() => {
            const next = accessor();
            if (!Object.is(next, lastValue)) {
                lastValue = next;
                emit([{ op: 'set-text', id: node.id, value: String(next ?? '') }]);
            }
        });
    };
    const snapshotNode = (node) => {
        const resolvedProps = shouldTrackProps(node) ? resolveProps(node.props) : {};
        if (node._theme) {
            resolvedProps[THEME_SCOPE_PROP] = node._theme;
        }
        return {
            id: node.id,
            type: getPatchType(node),
            props: Object.keys(resolvedProps).length > 0 ? resolvedProps : undefined,
            text: isReactiveTextCarrier(node)
                ? String(resolveTextNodeValue(node) ?? '')
                : node.text,
            key: node.key,
            ...(node._runtimeComposedSchemaTypes && node._runtimeComposedSchemaTypes.length > 0
                ? { _runtimeComposedSchemaTypes: [...node._runtimeComposedSchemaTypes] }
                : {}),
        };
    };
    const walk = (node, parentId, index, patches, inheritedTheme = undefined) => {
        const previousTheme = getTheme();
        const normalized = normalizeNode(node);
        const nextTheme = normalized._theme ?? inheritedTheme;
        if (nextTheme && !Object.is(nextTheme, getTheme())) {
            setTheme(nextTheme);
        }
        const scopedNode = nextTheme && normalized._theme !== nextTheme
            ? { ...normalized, _theme: nextTheme }
            : normalized;
        nodeMap.set(scopedNode.id, scopedNode);
        const snapshot = snapshotNode(scopedNode);
        patches.push({ op: 'create', node: snapshot });
        if (parentId !== null && index !== null) {
            patches.push({ op: 'insert-child', id: parentId, childId: scopedNode.id, index });
        }
        if (isControlFlowNode(scopedNode)) {
            mountControlChildren(scopedNode, patches);
            if (!Object.is(getTheme(), previousTheme)) {
                setTheme(previousTheme);
            }
            return scopedNode;
        }
        mountNodeScope(scopedNode);
        const childNodes = scopedNode.children ?? [];
        childNodes.forEach((child, childIndex) => {
            childNodes[childIndex] = walk(child, scopedNode.id, childIndex, patches, nextTheme);
        });
        if (!Object.is(getTheme(), previousTheme)) {
            setTheme(previousTheme);
        }
        return scopedNode;
    };
    const registerControlEffect = (node, skipInitialDiff) => {
        let initialized = false;
        effect(() => {
            if (!initialized && skipInitialDiff) {
                initialized = true;
                const baselineChildren = controlChildren.get(node.id) ?? [];
                const mountedSelection = controlSelections.get(node.id);
                const observedSelection = controlFlowRuntime.describeSelection(node);
                // Keyed For caches make a second selection read cheap and let us catch
                // in-place collection mutations that happen while children materialize.
                if (node.type !== 'For' && Object.is(mountedSelection, observedSelection)) {
                    return;
                }
                schedulePostCommitTask(() => {
                    schedule(() => {
                        if (disposed) {
                            return;
                        }
                        const currentChildren = controlChildren.get(node.id) ?? [];
                        if (currentChildren !== baselineChildren) {
                            return;
                        }
                        const nextChildren = controlFlowRuntime.resolveChildren(node, keyValidation);
                        const diff = diffChildren(node.id, currentChildren, nextChildren);
                        if (diff.patches.length === 0) {
                            return;
                        }
                        controlChildren.set(node.id, diff.resolvedChildren);
                        controlSelections.set(node.id, controlFlowRuntime.describeSelection(node));
                        nodeMap.set(node.id, node);
                        emit(diff.patches);
                    });
                });
                return;
            }
            const nextSelection = controlFlowRuntime.describeSelection(node);
            const nextChildren = controlFlowRuntime.resolveChildren(node, keyValidation);
            const prevChildren = controlChildren.get(node.id) ?? [];
            if (!initialized) {
                initialized = true;
                if (skipInitialDiff) {
                    // Control-flow effects can fire during mount; skip the initial diff to avoid
                    // patches racing ahead of the initial create/insert batch. If we detect a
                    // mismatch, schedule a microtask diff guarded against newer updates.
                    const merge = mergeChildren(prevChildren, nextChildren, {
                        ensureNodeId,
                        getComparableType,
                        shouldTrackProps,
                        resolveStaticProps,
                    });
                    controlChildren.set(node.id, merge.children);
                    nodeMap.set(node.id, node);
                    if (merge.needsPostMountDiff) {
                        const pendingChildren = nextChildren;
                        const baselineChildren = merge.children;
                        schedule(() => {
                            if (disposed || !nodeMap.has(node.id)) {
                                return;
                            }
                            if (controlChildren.get(node.id) !== baselineChildren) {
                                return;
                            }
                            const diff = diffChildren(node.id, baselineChildren, pendingChildren);
                            controlChildren.set(node.id, diff.resolvedChildren);
                            nodeMap.set(node.id, node);
                            emit(diff.patches);
                        });
                    }
                    return;
                }
            }
            const diff = untrack(() => diffChildren(node.id, prevChildren, nextChildren));
            controlChildren.set(node.id, diff.resolvedChildren);
            controlSelections.set(node.id, nextSelection);
            nodeMap.set(node.id, node);
            emit(diff.patches);
        });
    };
    const mountControlChildren = (node, patches) => {
        const mountedSelection = controlFlowRuntime.describeSelection(node);
        const children = controlFlowRuntime.resolveChildren(node, keyValidation);
        const mountedChildren = children.map((child, childIndex) => walk(child, node.id, childIndex, patches));
        controlChildren.set(node.id, mountedChildren);
        controlSelections.set(node.id, mountedSelection);
        nodeMap.set(node.id, node);
        mountNodeScope(node, true);
    };
    const diffChildren = (parentId, prevChildren, nextChildrenRaw) => {
        const patches = [];
        const nextChildren = nextChildrenRaw.map((child) => ensureNodeId(child));
        const prevLength = prevChildren.length;
        const nextLength = nextChildren.length;
        if (prevLength === 0) {
            const resolvedChildren = nextChildren.map((child, index) => walk(child, parentId, index, patches));
            return { patches, resolvedChildren };
        }
        if (nextLength === 0) {
            for (const prev of prevChildren) {
                patches.push({ op: 'remove-child', id: parentId, childId: prev.id });
                patches.push({ op: 'remove', id: prev.id });
                disposeNode(prev.id);
            }
            return { patches, resolvedChildren: [] };
        }
        const resolvedNext = new Array(nextLength);
        const prevIndices = new Int32Array(nextLength);
        prevIndices.fill(-1);
        const usedPrev = new Uint8Array(prevLength);
        const reuse = (prev, next, prevIndex, nextIndex) => {
            const reused = prev === next ? prev : { ...next, id: prev.id };
            usedPrev[prevIndex] = 1;
            prevIndices[nextIndex] = prevIndex;
            resolvedNext[nextIndex] = reused;
            if (prev !== next) {
                resetNodeBindings(prev.id, reused);
            }
            nodeMap.set(prev.id, reused);
        };
        let start = 0;
        let prevEnd = prevLength - 1;
        let nextEnd = nextLength - 1;
        while (start <= prevEnd && start <= nextEnd) {
            const prev = prevChildren[start];
            const next = nextChildren[start];
            if (!prev || !next || !canReuseChild(prev, next, start, start)) {
                break;
            }
            reuse(prev, next, start, start);
            start += 1;
        }
        while (prevEnd >= start && nextEnd >= start) {
            const prev = prevChildren[prevEnd];
            const next = nextChildren[nextEnd];
            if (!prev || !next || !canReuseChild(prev, next, prevEnd, nextEnd)) {
                break;
            }
            reuse(prev, next, prevEnd, nextEnd);
            prevEnd -= 1;
            nextEnd -= 1;
        }
        const prevRemaining = prevEnd - start + 1;
        const nextRemaining = nextEnd - start + 1;
        if (prevRemaining > 0 && nextRemaining > 0) {
            if (nextRemaining < 4 || (prevRemaining | nextRemaining) < 32) {
                for (let nextIndex = start; nextIndex <= nextEnd; nextIndex += 1) {
                    const next = nextChildren[nextIndex];
                    if (!next) {
                        continue;
                    }
                    for (let prevIndex = start; prevIndex <= prevEnd; prevIndex += 1) {
                        const prev = prevChildren[prevIndex];
                        if (prev
                            && usedPrev[prevIndex] === 0
                            && canReuseChild(prev, next, prevIndex, nextIndex)) {
                            reuse(prev, next, prevIndex, nextIndex);
                            break;
                        }
                    }
                }
            }
            else {
                const prevByKey = new Map();
                const prevById = new Map();
                for (let prevIndex = start; prevIndex <= prevEnd; prevIndex += 1) {
                    const prev = prevChildren[prevIndex];
                    if (!prev) {
                        continue;
                    }
                    if (prev.key !== undefined && prev.key !== null) {
                        prevByKey.set(prev.key, prevIndex);
                    }
                    else {
                        prevById.set(prev.id, prevIndex);
                    }
                }
                for (let nextIndex = start; nextIndex <= nextEnd; nextIndex += 1) {
                    const next = nextChildren[nextIndex];
                    if (!next) {
                        continue;
                    }
                    const candidateIndex = next.key !== undefined && next.key !== null
                        ? prevByKey.get(next.key)
                        : prevById.get(next.id);
                    if (candidateIndex !== undefined) {
                        const candidate = prevChildren[candidateIndex];
                        if (candidate
                            && usedPrev[candidateIndex] === 0
                            && canReuseChild(candidate, next, candidateIndex, nextIndex)) {
                            reuse(candidate, next, candidateIndex, nextIndex);
                            continue;
                        }
                    }
                    const prevAtIndex = prevChildren[nextIndex];
                    if (prevAtIndex
                        && usedPrev[nextIndex] === 0
                        && canReuseChild(prevAtIndex, next, nextIndex, nextIndex)) {
                        reuse(prevAtIndex, next, nextIndex, nextIndex);
                    }
                }
            }
        }
        for (let index = 0; index < nextLength; index += 1) {
            const next = nextChildren[index];
            if (next) {
                resolvedNext[index] ?? (resolvedNext[index] = next);
            }
        }
        for (let prevIndex = 0; prevIndex < prevLength; prevIndex += 1) {
            if (usedPrev[prevIndex] === 0) {
                const prev = prevChildren[prevIndex];
                if (!prev) {
                    continue;
                }
                patches.push({ op: 'remove-child', id: parentId, childId: prev.id });
                patches.push({ op: 'remove', id: prev.id });
                disposeNode(prev.id);
            }
        }
        let moved = false;
        let lastPrevIndex = -1;
        for (let index = 0; index < nextLength; index += 1) {
            const prevIndex = prevIndices[index] ?? -1;
            if (prevIndex < 0) {
                continue;
            }
            if (prevIndex < lastPrevIndex) {
                moved = true;
                break;
            }
            lastPrevIndex = prevIndex;
        }
        const stable = moved ? longestIncreasingSubsequenceIndices(prevIndices) : null;
        resolvedNext.forEach((child, index) => {
            const prevIndex = prevIndices[index] ?? -1;
            if (prevIndex < 0) {
                resolvedNext[index] = walk(child, parentId, index, patches);
                return;
            }
            const prev = prevChildren[prevIndex];
            if (!prev) {
                return;
            }
            if (stable && !stable.has(index) && prevChildren[index]?.id !== child.id) {
                patches.push({ op: 'move-child', id: parentId, childId: child.id, index });
            }
            if (isControlFlowNode(child)) {
                const prevControlChildren = controlChildren.get(child.id) ?? [];
                const { nextControlChildren, nextSelection } = untrack(() => ({
                    nextControlChildren: controlFlowRuntime.resolveChildren(child, keyValidation),
                    nextSelection: controlFlowRuntime.describeSelection(child),
                }));
                const childDiff = untrack(() => diffChildren(child.id, prevControlChildren, nextControlChildren));
                controlChildren.set(child.id, childDiff.resolvedChildren);
                controlSelections.set(child.id, nextSelection);
                if (childDiff.patches.length > 0) {
                    patches.push(...childDiff.patches);
                }
            }
            else {
                const propUpdates = diffPropObjects(prev, child, {
                    shouldTrackProps,
                    resolveStaticProps,
                });
                if (propUpdates.length > 0) {
                    patches.push(...propUpdates);
                }
                const childDiff = diffChildren(child.id, prev.children ?? [], child.children ?? []);
                child.children = childDiff.resolvedChildren;
                nodeMap.set(child.id, child);
                if (childDiff.patches.length > 0) {
                    patches.push(...childDiff.patches);
                }
            }
        });
        return { patches, resolvedChildren: resolvedNext };
    };
    const resetNodeBindings = (nodeId, node) => {
        disposeNodeScope(nodeId);
        mountNodeScope(node, isControlFlowNode(node));
    };
    const disposeNode = (nodeId) => {
        const node = nodeMap.get(nodeId);
        if (node) {
            if (isControlFlowNode(node)) {
                controlFlowRuntime.disposeNode(nodeId);
                const children = controlChildren.get(nodeId) ?? [];
                children.forEach((child) => disposeNode(child.id));
                controlChildren.delete(nodeId);
                controlSelections.delete(nodeId);
            }
            else {
                node.children.forEach((child) => disposeNode(child.id));
            }
        }
        disposeNodeScope(nodeId);
        nodeMap.delete(nodeId);
    };
    return {
        mount(root, sink) {
            if (disposed) {
                return;
            }
            createRoot((dispose) => {
                disposeEmitterScope = dispose;
                emitterOwner = getOwner();
                scheduler = createPatchScheduler({
                    sink,
                    lane: options.lane,
                    now: options.now,
                    schedule: options.schedule,
                    flushContext,
                });
                const patches = [];
                walk(root, null, null, patches);
                emit(patches);
            }, { flushContext });
        },
        dispose() {
            if (disposed) {
                return;
            }
            disposed = true;
            disposeEmitterScope?.();
            disposeEmitterScope = null;
            emitterOwner = null;
            nodeScopes.clear();
            controlChildren.clear();
            controlSelections.clear();
            controlFlowRuntime.disposeAll();
            warnedForNodes.clear();
            warnedForDuplicateKeys.clear();
            nodeMap.clear();
            scheduler?.dispose();
            scheduler = null;
            disposeFlushContext(flushContext);
        },
    };
}
function isEventProp(name, value) {
    return typeof value === 'function' && !isReactiveAccessor(value) && name.startsWith('on');
}
function shouldTrackProps(node) {
    if (node.type === 'TextNode' || isLegacyReactiveValueNode(node)) {
        return false;
    }
    if (isControlFlowNode(node)) {
        return false;
    }
    return true;
}
function nodeNeedsScope(node, includeControlEffect = false) {
    if (node._runtimeDispose) {
        return true;
    }
    if (node.type === 'TextNode' || isLegacyReactiveValueNode(node)) {
        return isReactiveTextCarrier(node);
    }
    if (isControlFlowNode(node)) {
        return includeControlEffect;
    }
    if (!shouldTrackProps(node)) {
        return false;
    }
    const props = node.props ?? {};
    for (const [name, value] of Object.entries(props)) {
        if (isReactiveAccessor(value) && !isEventProp(name, value)) {
            return true;
        }
    }
    return false;
}
function resolveProps(props) {
    const resolved = {};
    if (!props) {
        return resolved;
    }
    for (const [name, value] of Object.entries(props)) {
        const result = resolvePropValue(name, value);
        if (result !== undefined) {
            resolved[name] = result;
        }
    }
    return resolved;
}
function resolvePropValue(name, value) {
    if (isReactiveAccessor(value) && !isEventProp(name, value)) {
        return value();
    }
    return value;
}
function resolveStaticProps(props) {
    const resolved = {};
    if (!props) {
        return resolved;
    }
    for (const [name, value] of Object.entries(props)) {
        if (isReactiveAccessor(value) && !isEventProp(name, value)) {
            continue;
        }
        if (value !== undefined) {
            resolved[name] = value;
        }
    }
    return resolved;
}
function isReactiveTextAccessor(value) {
    return typeof value === 'function';
}
function isReactiveTextNode(node) {
    return node.type === 'TextNode' && isReactiveTextAccessor(node.props?.value);
}
function isLegacyReactiveValueNode(node) {
    return node.type === 'ReactiveValue';
}
function isReactiveTextCarrier(node) {
    return isReactiveTextNode(node) || (isLegacyReactiveValueNode(node) && isReactiveTextAccessor(node.props?.value));
}
function resolveTextNodeValue(node) {
    const value = node.props?.value;
    if (isReactiveTextCarrier(node) && isReactiveTextAccessor(value)) {
        return value();
    }
    return node.text;
}
function normalizeNode(node) {
    const withId = node.id ? node : { ...node, id: createNodeId() };
    return materializeRuntimeNode(withId);
}
// ensureNodeId removed - was identical to normalizeNode
const ensureNodeId = normalizeNode;
function getPatchType(node) {
    if (!node.type) {
        return 'Fragment';
    }
    if (isLegacyReactiveValueNode(node)) {
        return 'TextNode';
    }
    if (isControlFlowNode(node)) {
        return 'Fragment';
    }
    return node.type;
}
function getComparableType(node) {
    if (!node.type) {
        return 'Fragment';
    }
    if (isLegacyReactiveValueNode(node)) {
        return 'TextNode';
    }
    if (isControlFlowNode(node)) {
        return node.type;
    }
    return node.type;
}
function canReuseChild(prev, next, prevIndex, nextIndex) {
    if (getComparableType(prev) !== getComparableType(next)) {
        return false;
    }
    if (next.key !== undefined && next.key !== null) {
        return prev.key === next.key;
    }
    if (prev.key !== undefined && prev.key !== null) {
        return false;
    }
    return prev.id === next.id
        || (prevIndex === nextIndex
            && isControlFlowNode(prev)
            && isControlFlowNode(next));
}
//# sourceMappingURL=emitter.js.map